if (!customElements.get('variant-selects')) {
  customElements.define(
    'variant-selects',
    class VariantSelects extends HTMLElement {
      constructor() {
        super();
        this.productData = null;
      }

      async connectedCallback() {
        this.initPromise = this.loadProductData().then(() => {
          this.addEventListener('change', async (event) => {
          const rawTarget = event.target;
          const target = this.getInputForEventTarget(rawTarget);
          if (!target) return;

          this.syncDomSelection(rawTarget);
          this.updateSelectedSwatchValue({ target: rawTarget });

          const correctedOptionValueIds = this.getPublishedOptionValueIds(target);

          FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.optionValueSelectionChange, {
            data: {
              event,
              target: this.getInputForEventTarget(rawTarget),
              selectedOptionValues: correctedOptionValueIds,
            },
          });
        });
        });
        await this.initPromise;
      }

      async loadProductData() {
        if (this.productData) return this.productData;

        try {
          const productUrl = this.dataset.url;
          if (!productUrl) return null;

          const response = await fetch(`${productUrl}.js`);
          if (!response.ok) return null;

          this.productData = await response.json();
          return this.productData;
        } catch (error) {
          console.error('Failed to load product data for variant path auto-selection:', error);
          return null;
        }
      }

      get optionGroups() {
        return Array.from(this.querySelectorAll('.product-form__input'));
      }

      get selectedOptionValues() {
        return this.optionGroups
          .map((group) => {
            const checkedRadio = group.querySelector('input[type="radio"]:checked');
            if (checkedRadio) return checkedRadio.dataset.optionValueId || null;

            const select = group.querySelector('select');
            if (select?.selectedOptions?.[0]) {
              return select.selectedOptions[0].dataset.optionValueId || null;
            }

            return null;
          })
          .filter(Boolean);
      }

      getInputForEventTarget(target) {
        return target.tagName === 'SELECT' ? target.selectedOptions[0] : target;
      }

      getCurrentSelections() {
        return this.optionGroups.map((group) => {
          const checkedRadio = group.querySelector('input[type="radio"]:checked');
          if (checkedRadio) return checkedRadio.value;

          const select = group.querySelector('select');
          if (select) return select.value;

          return null;
        });
      }

      syncDomSelection(target) {
        if (target.tagName === 'SELECT') {
          Array.from(target.options).forEach((option) => option.removeAttribute('selected'));
          if (target.selectedOptions[0]) {
            target.selectedOptions[0].setAttribute('selected', 'selected');
          }
          return;
        }

        if (target.tagName === 'INPUT' && target.type === 'radio') {
          const selector = `input[type="radio"][name="${CSS.escape(target.name)}"]`;
          Array.from(this.querySelectorAll(selector)).forEach((input) => {
            input.checked = false;
            input.removeAttribute('checked');
          });

          target.checked = true;
          target.setAttribute('checked', 'checked');
        }
      }

      updateSelectedSwatchValue({ target }) {
        const { value, tagName } = target;

        if (tagName === 'SELECT' && target.selectedOptions.length) {
          Array.from(target.options)
            .find((option) => option.getAttribute('selected'))
            ?.removeAttribute('selected');

          target.selectedOptions[0].setAttribute('selected', 'selected');

          const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
          const selectedDropdownSwatchValue = target
            .closest('.product-form__input')
            .querySelector('[data-selected-value] > .swatch');

          if (!selectedDropdownSwatchValue) return;

          if (swatchValue) {
            selectedDropdownSwatchValue.style.setProperty('--swatch--background', swatchValue);
            selectedDropdownSwatchValue.classList.remove('swatch--unavailable');
          } else {
            selectedDropdownSwatchValue.style.setProperty('--swatch--background', 'unset');
            selectedDropdownSwatchValue.classList.add('swatch--unavailable');
          }

          selectedDropdownSwatchValue.style.setProperty(
            '--swatch-focal-point',
            target.selectedOptions[0].dataset.optionSwatchFocalPoint || 'unset'
          );
        } else if (tagName === 'INPUT' && target.type === 'radio') {
          const selectedSwatchValue = target.closest('.product-form__input')?.querySelector('[data-selected-value]');
          if (selectedSwatchValue) selectedSwatchValue.innerHTML = value;
        }
      }

      getPublishedOptionValueIds(target) {
        if (!this.productData?.variants?.length) {
          return this.selectedOptionValues;
        }

        const changedGroup = target.closest('.product-form__input');
        if (!changedGroup) {
          return this.selectedOptionValues;
        }

        const groups = this.optionGroups;
        const changedIndex = groups.indexOf(changedGroup);
        if (changedIndex === -1) {
          return this.selectedOptionValues;
        }

        const currentSelections = this.getCurrentSelections();
        const matchingVariant = this.findFirstMatchingVariantForChangedPath(currentSelections, changedIndex);
        window.matchingVariant = matchingVariant;

        if (matchingVariant && Array.isArray(matchingVariant.option_value_ids) && matchingVariant.option_value_ids.length) {
          return matchingVariant.option_value_ids.map(String);
        }

        return this.selectedOptionValues;
      }

      findFirstMatchingVariantForChangedPath(selections, changedIndex) {
        return this.productData.variants.find((variant) => {
          const variantOptions = variant.options || [variant.option1, variant.option2, variant.option3];

          for (let i = 0; i <= changedIndex; i++) {
            if (!selections[i]) continue;
            if (variantOptions[i] !== selections[i]) return false;
          }

          return true;
        });
      }
    }
  );
}